import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Filter, Plus, Save, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const PlanningBoard = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState('month'); // 'month' or 'week' (2 weeks)
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [resourcePlanningAssignments, setResourcePlanningAssignments] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null); // { personId, date }
  const [noteText, setNoteText] = useState('');
  const [dragState, setDragState] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    projects: [],
    people: [],
    projectTypes: [],
    dateRange: { start: '', end: '' }
  });
  const [weeklyFilters, setWeeklyFilters] = useState({});
  const gridRef = useRef(null);

  // Mock data - replace with API calls
  const mockPeople = [
    { id: 1, name: 'Jan Kowalski', role: 'Senior Developer' },
    { id: 2, name: 'Anna Nowak', role: 'Project Manager' },
    { id: 3, name: 'Piotr Wiśniewski', role: 'Designer' },
    { id: 4, name: 'Maria Kowalczyk', role: 'Tester' },
    { id: 5, name: 'Tomasz Kaczmarek', role: 'Developer' }
  ];

  const mockProjects = [
    { id: 1, name: 'Projekt Windpark A', color: '#3B82F6', type: 'project' },
    { id: 2, name: 'Turbina Serwis B', color: '#EF4444', type: 'project' },
    { id: 3, name: 'Inspekcja C', color: '#10B981', type: 'project' },
    { id: 4, name: 'Szkolenie PPE', color: '#F59E0B', type: 'project' }
  ];
  
  const additionalProjectTypes = [
    { id: 'travel-1', name: 'Travel - Conference Berlin', color: '#8B5CF6', type: 'travel' },
    { id: 'travel-2', name: 'Travel - Client Meeting', color: '#A855F7', type: 'travel' },
    { id: 'holiday-1', name: 'Holiday - Summer Break', color: '#06B6D4', type: 'holiday' },
    { id: 'holiday-2', name: 'Holiday - Christmas', color: '#0891B2', type: 'holiday' },
    { id: 'dayoff-1', name: 'Day Off - Personal', color: '#84CC16', type: 'day_off' },
    { id: 'maintenance-1', name: 'Equipment Maintenance', color: '#F97316', type: 'maintenance' }
  ];

  // Get week number (ISO week) - Monday as first day of week
  const getWeekNumber = (date) => {
    // Create a copy of the date
    const d = new Date(date.getTime());
    
    // Set to nearest Thursday: current date + 4 - current day number
    // Make Sunday's day number 7
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    
    // Get first day of year
    const yearStart = new Date(d.getFullYear(), 0, 1);
    
    // Calculate full weeks to nearest Thursday
    const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    
    return weekNo;
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Load real data from API
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      const [projectsResponse, usersResponse, assignmentsResponse, trainingsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/projects/`, { headers }),
        axios.get(`${API_BASE_URL}/api/users/`, { headers }),
        axios.get(`${API_BASE_URL}/api/resource-planning-assignments/`, { headers }),
        axios.get(`${API_BASE_URL}/api/trainings/`, { headers })
      ]);

      setProjects([...projectsResponse.data, ...additionalProjectTypes]);
      setSelectedPeople(usersResponse.data.map(user => ({
        id: user.id,
        name: user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.username,
        role: user.role || 'Team Member'
      })));
      setResourcePlanningAssignments(assignmentsResponse.data);
      setTrainings(trainingsResponse.data);

      console.log('📊 Data loaded:', {
        projects: projectsResponse.data.length,
        users: usersResponse.data.length,
        resourcePlanningAssignments: assignmentsResponse.data.length,
        trainings: trainingsResponse.data.length
      });

      console.log('🎓 Training data sample:', trainingsResponse.data.slice(0, 2));
      console.log('📋 Resource planning assignments sample:', assignmentsResponse.data.slice(0, 2));

    } catch (error) {
      console.error('Error loading data:', error);
      // Fallback to mock data
      setSelectedPeople(mockPeople);
      setProjects([...mockProjects, ...additionalProjectTypes]);
    } finally {
      setLoading(false);
    }
  };

  // Get 2 week dates
  const getTwoWeekDates = (date) => {
    const twoWeeks = [];
    const startDate = new Date(date);
    const day = startDate.getDay();
    const diff = startDate.getDate() - day + (day === 0 ? -6 : 1); // Start from Monday
    startDate.setDate(diff);

    for (let i = 0; i < 14; i++) { // 2 weeks = 14 days
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      twoWeeks.push(currentDate);
    }
    return twoWeeks;
  };

  // Get month dates (multiple weeks) - improved version
  const getMonthDates = (date) => {
    const monthDates = [];
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    
    // Start from Monday of the week containing first day of month
    const startDate = new Date(startOfMonth);
    const dayOfWeek = startDate.getDay();
    // Convert Sunday (0) to 7, then calculate offset to Monday
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(startDate.getDate() + daysToMonday);

    // End at Sunday of the week containing last day of month  
    const endDate = new Date(endOfMonth);
    const endDayOfWeek = endDate.getDay();
    // Convert Sunday (0) to 7, then calculate days to Sunday
    const daysToSunday = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
    endDate.setDate(endDate.getDate() + daysToSunday);

    // Generate all dates from Monday to Sunday
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      monthDates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log('Month dates generated:', monthDates.length, 'days from', monthDates[0].toLocaleDateString(), 'to', monthDates[monthDates.length-1].toLocaleDateString());
    console.log('First week number:', getWeekNumber(monthDates[0]), 'Last week number:', getWeekNumber(monthDates[monthDates.length-1]));
    
    return monthDates;
  };

  const twoWeekDates = getTwoWeekDates(currentDate);
  const monthDates = getMonthDates(currentDate);

  // Navigation functions
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'month') {
      newDate.setMonth(currentDate.getMonth() - 1);
    } else if (viewType === 'week') {
      newDate.setDate(currentDate.getDate() - 14); // Move by 2 weeks
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'month') {
      newDate.setMonth(currentDate.getMonth() + 1);
    } else if (viewType === 'week') {
      newDate.setDate(currentDate.getDate() + 14); // Move by 2 weeks
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Drag and drop handlers
  const handleMouseDown = (personId, timeSlot, event) => {
    setDragState({
      personId,
      startSlot: timeSlot,
      currentSlot: timeSlot,
      isActive: true
    });
    event.preventDefault();
  };

  const handleMouseEnter = (personId, timeSlot) => {
    if (dragState && dragState.isActive && dragState.personId === personId) {
      setDragState(prev => ({
        ...prev,
        currentSlot: timeSlot
      }));
    }
  };

  const handleMouseUp = () => {
    if (dragState && dragState.isActive) {
      // Create new assignment
      const newAssignment = {
        id: Date.now(),
        personId: dragState.personId,
        startSlot: Math.min(dragState.startSlot, dragState.currentSlot),
        endSlot: Math.max(dragState.startSlot, dragState.currentSlot),
        projectId: projects[0]?.id, // Default to first project
        title: projects[0]?.name || 'New Assignment'
      };
      setAssignments(prev => [...prev, newAssignment]);
    }
    setDragState(null);
  };

  // Check if a cell is selected in drag operation
  const isCellSelected = (personId, slotIndex) => {
    if (!dragState || !dragState.isActive || dragState.personId !== personId) {
      return false;
    }
    const start = Math.min(dragState.startSlot, dragState.currentSlot);
    const end = Math.max(dragState.startSlot, dragState.currentSlot);
    return slotIndex >= start && slotIndex <= end;
  };

  // Check if dragging would create conflict on this cell
  const wouldCreateConflict = (personId, slotIndex) => {
    if (!dragState || !dragState.isActive || dragState.personId !== personId) {
      return false;
    }

    const start = Math.min(dragState.startSlot, dragState.currentSlot);
    const end = Math.max(dragState.startSlot, dragState.currentSlot);

    // If this cell is in drag selection
    if (slotIndex >= start && slotIndex <= end) {
      const date = timeSlots[slotIndex];
      if (!date) return false;

      const existingAssignments = getExistingAssignmentsForPersonOnDate(personId, date);
      const existingTrainings = getExistingTrainingsForPersonOnDate(personId, date);

      const hasConflict = (existingAssignments.length + existingTrainings.length) > 0;

      if (hasConflict) {
        console.log('🚨 DRAG CONFLICT DETECTED:', {
          personId,
          date: date.toISOString().split('T')[0],
          existingAssignments: existingAssignments.length,
          existingTrainings: existingTrainings.length,
          trainings: existingTrainings.map(t => t.name || 'Unnamed')
        });
      }

      // There's a conflict if there are existing assignments or trainings
      return hasConflict;
    }

    return false;
  };

  // Check if a date falls within an assignment period
  const isDateInAssignment = (date, startDate, endDate) => {
    const dateStr = date.toISOString().split('T')[0];
    return dateStr >= startDate && dateStr <= endDate;
  };

  // Get existing resource planning assignments for a person on a specific date
  const getExistingAssignmentsForPersonOnDate = (personId, date) => {
    const planningAssignments = resourcePlanningAssignments.filter(assignment =>
      assignment.user === personId &&
      isDateInAssignment(date, assignment.start_date, assignment.end_date)
    );

    return planningAssignments;
  };

  // Get existing trainings for a person on a specific date
  const getExistingTrainingsForPersonOnDate = (personId, date) => {
    return trainings.filter(training => {
      // Check if user is assigned to this training
      const isUserAssigned = training.users && training.users.includes(personId);
      if (!isUserAssigned) return false;

      // Check if date falls within training period
      const startDate = new Date(training.start_date).toISOString().split('T')[0];
      const endDate = new Date(training.end_date).toISOString().split('T')[0];
      const checkDate = date.toISOString().split('T')[0];

      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  // Check for conflicts when creating new assignment
  const checkForConflicts = (personId, startDate, endDate, projectId) => {
    console.log('🔍 checkForConflicts called with:', {
      personId,
      startDate,
      endDate,
      projectId,
      resourcePlanningAssignmentsCount: resourcePlanningAssignments.length,
      trainingsCount: trainings.length
    });

    const conflicts = [];

    // Check each date in the range
    const currentDate = new Date(startDate);
    const endDateObj = new Date(endDate);

    while (currentDate <= endDateObj) {
      console.log(`📅 Checking date: ${currentDate.toISOString().split('T')[0]}`);

      // Check for existing project assignments
      const existingAssignments = getExistingAssignmentsForPersonOnDate(personId, currentDate);
      console.log(`  📋 Found ${existingAssignments.length} existing assignments`);

      existingAssignments.forEach(existing => {
        console.log(`    📋 Assignment:`, existing);
        // Don't count assignments to the same project as conflicts
        if (existing.project !== projectId) {
          const project = projects.find(p => p.id === existing.project);
          const conflict = {
            date: new Date(currentDate),
            existingProject: project?.name || 'Unknown Project',
            existingProjectType: existing.travel_type || 'project',
            assignmentId: existing.id,
            conflictType: 'project'
          };
          conflicts.push(conflict);
          console.log(`    ⚠️ PROJECT CONFLICT:`, conflict);
        }
      });

      // Check for existing trainings
      const existingTrainings = getExistingTrainingsForPersonOnDate(personId, currentDate);
      console.log(`  🎓 Found ${existingTrainings.length} existing trainings`);

      existingTrainings.forEach(training => {
        console.log(`    🎓 Training:`, training);
        const trainingName = training.name ||
          `${training.training_center_qualification?.qualification_type?.qualification_type?.name || 'Training'}`;
        const conflict = {
          date: new Date(currentDate),
          existingProject: trainingName,
          existingProjectType: 'training',
          assignmentId: training.id,
          conflictType: 'training'
        };
        conflicts.push(conflict);
        console.log(`    ⚠️ TRAINING CONFLICT:`, conflict);
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    console.log(`🏁 Total conflicts found: ${conflicts.length}`, conflicts);
    return conflicts;
  };

  // Check if a cell has an assignment (existing logic for drag assignments)
  const getAssignmentForCell = (personId, slotIndex) => {
    const dragAssignment = assignments.find(assignment =>
      assignment.personId === personId &&
      slotIndex >= assignment.startSlot &&
      slotIndex <= assignment.endSlot
    );

    if (dragAssignment) return dragAssignment;

    // Check for real resource planning assignments
    const date = timeSlots[slotIndex];
    if (!date) return null;

    const existingAssignments = getExistingAssignmentsForPersonOnDate(personId, date);
    const existingTrainings = getExistingTrainingsForPersonOnDate(personId, date);

    // Priority: Show trainings first, then project assignments
    if (existingTrainings.length > 0) {
      const training = existingTrainings[0]; // Show first training if multiple
      const trainingName = training.name ||
        `${training.training_center_qualification?.qualification_type?.qualification_type?.name || 'Training'}`;

      return {
        id: `training-${training.id}`,
        personId,
        projectId: `training-${training.id}`,
        title: trainingName,
        isTraining: true,
        startDate: training.start_date,
        endDate: training.end_date,
        location: training.training_center_qualification?.training_center?.name
      };
    }

    if (existingAssignments.length > 0) {
      const assignment = existingAssignments[0]; // Show first assignment if multiple
      const project = projects.find(p => p.id === assignment.project);

      return {
        id: `rpa-${assignment.id}`,
        personId,
        projectId: assignment.project,
        title: project?.name || 'Project Assignment',
        isResourcePlanningAssignment: true,
        travelType: assignment.travel_type,
        role: assignment.role,
        startDate: assignment.start_date,
        endDate: assignment.end_date
      };
    }

    return null;
  };

  // Enhanced mouse up handler with conflict detection
  const handleMouseUpWithConflictCheck = () => {
    console.log('🔍 handleMouseUpWithConflictCheck called', {
      dragState,
      hasDragState: !!dragState,
      isActive: dragState?.isActive
    });

    if (dragState && dragState.isActive) {
      const startSlotDate = timeSlots[Math.min(dragState.startSlot, dragState.currentSlot)];
      const endSlotDate = timeSlots[Math.max(dragState.startSlot, dragState.currentSlot)];

      console.log('📅 Date range:', {
        startSlotDate,
        endSlotDate,
        startSlot: dragState.startSlot,
        currentSlot: dragState.currentSlot
      });

      if (!startSlotDate || !endSlotDate) {
        console.log('❌ No valid dates found');
        setDragState(null);
        return;
      }

      const startDateStr = startSlotDate.toISOString().split('T')[0];
      const endDateStr = endSlotDate.toISOString().split('T')[0];
      const defaultProject = projects[0];

      console.log('🎯 Checking conflicts for:', {
        personId: dragState.personId,
        startDateStr,
        endDateStr,
        projectId: defaultProject?.id,
        projectName: defaultProject?.name
      });

      // Check for conflicts
      const conflicts = checkForConflicts(dragState.personId, startDateStr, endDateStr, defaultProject?.id);

      console.log('⚠️ Conflicts found:', conflicts);

      if (conflicts.length > 0) {
        // Show conflict warning
        const person = selectedPeople.find(p => p.id === dragState.personId);
        const personName = person?.name || 'Unknown Person';

        let conflictMessage = `⚠️ ASSIGNMENT CONFLICT DETECTED!\n\n`;
        conflictMessage += `Person: ${personName}\n`;
        conflictMessage += `New Assignment: ${defaultProject?.name || 'Project'} (${startDateStr} to ${endDateStr})\n\n`;
        conflictMessage += `Existing Assignments:\n`;

        conflicts.forEach(conflict => {
          conflictMessage += `• ${conflict.date.toLocaleDateString()}: ${conflict.existingProject}`;
          if (conflict.existingProjectType !== 'project') {
            conflictMessage += ` (${conflict.existingProjectType})`;
          }
          conflictMessage += `\n`;
        });

        conflictMessage += `\nDo you want to proceed anyway? This will create overlapping assignments.`;

        console.log('💬 Showing conflict dialog:', conflictMessage);
        const proceed = window.confirm(conflictMessage);
        console.log('👤 User decision:', proceed ? 'PROCEED' : 'CANCEL');

        if (!proceed) {
          setDragState(null);
          return;
        }
      } else {
        console.log('✅ No conflicts detected, creating assignment');
      }

      // Create new assignment (existing logic)
      const newAssignment = {
        id: Date.now(),
        personId: dragState.personId,
        startSlot: Math.min(dragState.startSlot, dragState.currentSlot),
        endSlot: Math.max(dragState.startSlot, dragState.currentSlot),
        projectId: defaultProject?.id,
        title: defaultProject?.name || 'New Assignment'
      };
      console.log('📝 Creating new assignment:', newAssignment);
      setAssignments(prev => [...prev, newAssignment]);
    }
    setDragState(null);
  };

  // Handle double click for notes
  const handleDoubleClick = (personId, slotIndex) => {
    const date = timeSlots[slotIndex];
    if (!date) return;

    // Don't allow notes on cells with assignments
    const assignment = getAssignmentForCell(personId, slotIndex);
    if (assignment) return;

    const existing = notes.find(note =>
      note.personId === personId &&
      note.date.getTime() === date.getTime()
    );

    setSelectedCell({ personId, date });
    setNoteText(existing ? existing.text : '');
    setShowNoteModal(true);
  };

  // Save note
  const handleSaveNote = () => {
    if (!selectedCell) return;

    const noteId = `${selectedCell.personId}-${selectedCell.date.getTime()}`;
    const newNote = {
      id: noteId,
      personId: selectedCell.personId,
      date: selectedCell.date,
      text: noteText,
      createdAt: new Date()
    };

    if (noteText.trim()) {
      setNotes(prev => {
        const filtered = prev.filter(note => note.id !== noteId);
        return [...filtered, newNote];
      });
    } else {
      // Remove note if text is empty
      setNotes(prev => prev.filter(note => note.id !== noteId));
    }

    setShowNoteModal(false);
    setSelectedCell(null);
    setNoteText('');
  };

  // Get note for cell
  const getNoteForCell = (personId, slotIndex) => {
    const date = timeSlots[slotIndex];
    if (!date) return null;

    return notes.find(note =>
      note.personId === personId &&
      note.date.getTime() === date.getTime()
    );
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('pl-PL', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatMonth = (date) => {
    return date.toLocaleDateString('pl-PL', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const timeSlots = viewType === 'month' ? monthDates : twoWeekDates;

  return (
    <div className="w-full h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900">Planning Board</h1>
            
            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewType('month')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  viewType === 'month' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Month
              </button>
              <button
                onClick={() => setViewType('week')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  viewType === 'week' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                2 Weeks
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center space-x-4">
            <button
              onClick={goToToday}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Today
            </button>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPrevious}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <h2 className="text-lg font-semibold text-gray-900 min-w-48 text-center">
                {formatMonth(currentDate)}
              </h2>
              
              <button
                onClick={goToNext}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md transition-colors ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="border-t border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center space-x-6">
              {/* Project Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Projects:</label>
                <select 
                  multiple
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  value={filters.projects}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    projects: Array.from(e.target.selectedOptions, option => option.value)
                  }))}
                >
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* People Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">People:</label>
                <select 
                  multiple
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  value={filters.people}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    people: Array.from(e.target.selectedOptions, option => option.value)
                  }))}
                >
                  {selectedPeople.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project Type Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Types:</label>
                <select 
                  multiple
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  value={filters.projectTypes}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    projectTypes: Array.from(e.target.selectedOptions, option => option.value)
                  }))}
                >
                  <option value="project">Projects</option>
                  <option value="travel">Travel</option>
                  <option value="holiday">Holiday</option>
                  <option value="day_off">Day Off</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => setFilters({ projects: [], people: [], projectTypes: [], dateRange: { start: '', end: '' } })}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Planning Grid */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex">
          {/* People Column - Fixed/Sticky */}
          <div className="w-64 bg-white border-r border-gray-200 flex-shrink-0 z-20">
            {/* Week Info Header */}
            <div className="h-8 bg-blue-50 border-b border-gray-200 flex items-center px-4">
              {viewType === 'month' && (
                <div className="text-xs font-medium text-blue-700">
                  {formatMonth(currentDate)}
                </div>
              )}
              {viewType === 'week' && (
                <div className="text-xs font-medium text-blue-700">
                  Tygodnie {getWeekNumber(timeSlots[0])} - {getWeekNumber(timeSlots[timeSlots.length - 1])} • {currentDate.getFullYear()}
                </div>
              )}
            </div>
            
            {/* People Header */}
            <div className="h-16 border-b border-gray-200 bg-gray-50 flex items-center px-4">
              <h3 className="font-semibold text-gray-900">Team Members</h3>
            </div>
            
            {/* People List */}
            <div className="overflow-y-auto" style={{ height: 'calc(100% - 6rem)' }}>
              {selectedPeople.map((person) => (
                <div
                  key={person.id}
                  className="h-16 border-b border-gray-100 px-4 flex items-center hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <div className="font-medium text-gray-900">{person.name}</div>
                    <div className="text-sm text-gray-500">{person.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline Grid - Scrollable */}
          <div className="flex-1 overflow-auto" ref={gridRef}>
            <div className="min-w-full">
              {/* Week Info Row */}
              <div className="h-8 bg-blue-50 border-b border-gray-200 flex sticky top-0 z-10">
                {(() => {
                  const weekGroups = [];
                  let currentWeek = null;
                  let weekStart = 0;
                  
                  // Group consecutive days by week number
                  timeSlots.forEach((slot, index) => {
                    const weekNum = getWeekNumber(slot);
                    if (currentWeek !== weekNum) {
                      if (currentWeek !== null) {
                        weekGroups.push({ 
                          weekNum: currentWeek, 
                          start: weekStart, 
                          end: index - 1,
                          dayCount: index - weekStart,
                          startDate: timeSlots[weekStart],
                          endDate: timeSlots[index - 1]
                        });
                      }
                      currentWeek = weekNum;
                      weekStart = index;
                    }
                  });
                  
                  // Add last group
                  if (currentWeek !== null) {
                    weekGroups.push({ 
                      weekNum: currentWeek, 
                      start: weekStart, 
                      end: timeSlots.length - 1,
                      dayCount: timeSlots.length - weekStart,
                      startDate: timeSlots[weekStart],
                      endDate: timeSlots[timeSlots.length - 1]
                    });
                  }
                  
                  // Debug log
                  if (viewType === 'month') {
                    console.log('Week groups for month view:', weekGroups.map(g => ({
                      week: g.weekNum,
                      days: g.dayCount,
                      start: g.startDate?.toLocaleDateString(),
                      end: g.endDate?.toLocaleDateString()
                    })));
                  }
                  
                  return weekGroups.map((group, groupIndex) => {
                    const width = (group.dayCount / timeSlots.length) * 100;
                    
                    return (
                      <div
                        key={`${group.weekNum}-${groupIndex}`}
                        className="border-r border-gray-200 flex items-center justify-between px-2"
                        style={{ width: `${width}%` }}
                      >
                        <span className="text-xs font-medium text-blue-700">
                          W{group.weekNum}
                        </span>
                        {viewType === 'month' && (
                          <div className="flex items-center space-x-1">
                            <select 
                              className="text-xs border rounded px-1 py-0.5"
                              value={weeklyFilters[group.weekNum] || ''}
                              onChange={(e) => setWeeklyFilters(prev => ({
                                ...prev,
                                [group.weekNum]: e.target.value
                              }))}
                            >
                              <option value="">All</option>
                              <option value="project">Project</option>
                              <option value="travel">Travel</option>
                              <option value="holiday">Holiday</option>
                              <option value="available">Available</option>
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Time Headers */}
              <div className="h-16 bg-gray-50 border-b border-gray-200 flex sticky top-8 z-10">
                {timeSlots.map((slot, index) => (
                  <div
                    key={index}
                    className="flex-1 min-w-32 border-r border-gray-200 flex items-center justify-center px-2"
                  >
                    <span className="font-medium text-gray-900 text-sm">
                      {formatDate(slot)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Grid Body */}
              <div
                className="relative"
                onMouseUp={handleMouseUpWithConflictCheck}
                onMouseLeave={() => setDragState(null)}
              >
{loading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-gray-500">Loading assignments...</div>
                  </div>
                ) : (
                  selectedPeople.map((person) => (
                    <div key={person.id} className="h-16 border-b border-gray-100 flex">
                      {timeSlots.map((slot, slotIndex) => {
                        const assignment = getAssignmentForCell(person.id, slotIndex);
                        const isSelected = isCellSelected(person.id, slotIndex);
                        const hasConflictDuringDrag = wouldCreateConflict(person.id, slotIndex);

                        // Check for multiple assignments on this date (conflict indication)
                        const existingAssignments = getExistingAssignmentsForPersonOnDate(person.id, slot);
                        const existingTrainings = getExistingTrainingsForPersonOnDate(person.id, slot);
                        const hasMultipleAssignments = (existingAssignments.length + existingTrainings.length) > 1;

                        const note = getNoteForCell(person.id, slotIndex);
                        let cellClassName = `flex-1 min-w-32 border-r border-gray-200 relative cursor-pointer transition-colors `;
                        let backgroundColor = projects.find(p => p.id === assignment?.projectId)?.color;

                        if (isSelected) {
                          if (hasConflictDuringDrag) {
                            cellClassName += 'bg-red-200 border-red-400 ';
                          } else {
                            cellClassName += 'bg-blue-200 ';
                          }
                        } else if (assignment) {
                          if (assignment.isTraining) {
                            cellClassName += 'bg-orange-100 hover:bg-orange-200 ';
                            backgroundColor = '#F97316';
                          } else if (assignment.isResourcePlanningAssignment) {
                            if (assignment.travelType === 'check_in' || assignment.travelType === 'check_out') {
                              cellClassName += 'bg-purple-100 hover:bg-purple-200 ';
                              backgroundColor = '#8B5CF6';
                            } else {
                              cellClassName += 'bg-blue-100 hover:bg-blue-200 ';
                            }
                          } else {
                            cellClassName += 'bg-green-100 hover:bg-green-200 ';
                          }

                          if (hasMultipleAssignments) {
                            cellClassName += 'ring-2 ring-red-400 ring-inset ';
                          }
                        } else if (note) {
                          cellClassName += 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200 ';
                        } else {
                          cellClassName += 'hover:bg-gray-50 ';
                        }

                        return (
                          <div
                            key={slotIndex}
                            className={cellClassName}
                            onMouseDown={(e) => handleMouseDown(person.id, slotIndex, e)}
                            onMouseEnter={() => handleMouseEnter(person.id, slotIndex)}
                            onDoubleClick={() => handleDoubleClick(person.id, slotIndex)}
                            title={(() => {
                              if (assignment) {
                                return `${assignment.title}${assignment.role ? ` - ${assignment.role}` : ''}${
                                  assignment.travelType ? ` (${assignment.travelType})` : ''
                                }${assignment.location ? ` at ${assignment.location}` : ''}${
                                  assignment.isTraining ? ' (Training)' : ''
                                }${hasMultipleAssignments ? ' - CONFLICT: Multiple assignments!' : ''}`;
                              }
                              const note = getNoteForCell(person.id, slotIndex);
                              if (note) {
                                return `Note: ${note.text}`;
                              }
                              return 'Double-click to add note';
                            })()}
                          >
                            {assignment && (
                              <div className="absolute inset-1 rounded px-2 py-1 text-xs font-medium text-white shadow-sm flex items-center justify-between"
                                   style={{ backgroundColor }}>
                                <span className="truncate flex-1">{assignment.title}</span>
                                {hasMultipleAssignments && (
                                  <AlertTriangle className="w-3 h-3 ml-1 text-red-200 flex-shrink-0" />
                                )}
                              </div>
                            )}

                            {!assignment && note && (
                              <div className="absolute inset-1 rounded px-2 py-1 text-xs text-gray-700 bg-yellow-100 border border-yellow-300">
                                <div className="truncate">📝 {note.text}</div>
                              </div>
                            )}

                            {hasConflictDuringDrag && (
                              <div className="absolute top-1 right-1">
                                <AlertTriangle className="w-4 h-4 text-red-500 bg-white rounded-full p-0.5" fill="currentColor" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Note Modal */}
      {showNoteModal && selectedCell && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add Note</h3>
                <button
                  onClick={() => {
                    setShowNoteModal(false);
                    setSelectedCell(null);
                    setNoteText('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <div className="text-sm text-gray-600 mb-2">
                  {(() => {
                    const person = selectedPeople.find(p => p.id === selectedCell.personId);
                    return `${person?.name || 'Unknown'} - ${selectedCell.date.toLocaleDateString()}`;
                  })()}
                </div>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Enter your note..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                  autoFocus
                />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setNoteText('');
                    handleSaveNote();
                  }}
                  className="px-4 py-2 text-red-600 hover:text-red-800 text-sm"
                  disabled={!noteText.trim()}
                >
                  Delete Note
                </button>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowNoteModal(false);
                      setSelectedCell(null);
                      setNoteText('');
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNote}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Projects Legend */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex items-center space-x-6 mb-3">
          <span className="font-medium text-gray-900">Projects:</span>
          {projects.map((project) => (
            <div key={project.id} className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: project.color }}
              />
              <span className="text-sm text-gray-700">{project.name}</span>
            </div>
          ))}
        </div>

        {/* Assignment Types Legend */}
        <div className="flex items-center space-x-6 text-sm">
          <span className="font-medium text-gray-900">Types:</span>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-orange-400 rounded"></div>
            <span className="text-gray-700">Training</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-400 rounded"></div>
            <span className="text-gray-700">Project Assignment</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-purple-400 rounded"></div>
            <span className="text-gray-700">Travel</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-400 rounded"></div>
            <span className="text-gray-700">New Assignment</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-200 rounded border border-yellow-300"></div>
            <span className="text-gray-700">Notes (Double-click to add)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanningBoard;